import { useApiClient } from '@/composables/useApiClient'

export interface SttTranscriptionSegment {
  speaker_id: string | null
  text: string
  start: number | null
  end: number | null
  type: string | null
}

export interface SttTranscriptionResponse {
  provider: string
  model: string
  domain_code: string
  transcription_text: string | null
  transcription_segments: SttTranscriptionSegment[]
  language: string | null
  duration_seconds: number
  usage_units: number
  call_ai_analysis_id: string | null
}

export interface RealtimeTranscriptItem {
  speaker: 'agent' | 'customer'
  text: string
}

export interface RealtimeSuggestionsRequest {
  campaign_id: string
  usage_group_id?: string | null
  transcript: RealtimeTranscriptItem[]
  current_summary?: string | null
  domain_code?: string | null
}

export interface RealtimeSuggestionsResponse {
  suggestions: string[]
}

export interface RealtimeSummaryRequest {
  campaign_id: string
  usage_group_id?: string | null
  transcript: RealtimeTranscriptItem[]
  previous_summary?: string | null
  final?: boolean
  domain_code?: string | null
}

export interface RealtimeSummaryResponse {
  summary: string
}

export interface RealtimeCallUsageGroupRequest {
  campaign_id: string
  session_id: string
}

export interface RealtimeCallUsageGroupResponse {
  usage_group_id: string
}

export const useCallSttService = () => {
  const privateApi = useApiClient(true)

  const transcribeChunk = async (file: File, campaignId: string, usageGroupId: string) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('campaign_id', campaignId)
    formData.append('diarize', 'false')
    formData.append('usage_group_id', usageGroupId)

    return privateApi.post<SttTranscriptionResponse, FormData>(
      '/ai/test/stt-transcription',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
  }

  const generateRealtimeSuggestions = async (payload: RealtimeSuggestionsRequest) =>
    privateApi.post<RealtimeSuggestionsResponse, RealtimeSuggestionsRequest>(
      '/ai/test/realtime-suggestions',
      payload,
    )

  const generateRealtimeSummary = async (payload: RealtimeSummaryRequest) =>
    privateApi.post<RealtimeSummaryResponse, RealtimeSummaryRequest>(
      '/ai/test/realtime-summary',
      payload,
    )

  const createRealtimeCallUsageGroup = (payload: RealtimeCallUsageGroupRequest) =>
    privateApi.post<RealtimeCallUsageGroupResponse, RealtimeCallUsageGroupRequest>(
      '/ai/realtime-calls/usage-groups',
      payload,
    )

  return {
    createRealtimeCallUsageGroup,
    generateRealtimeSuggestions,
    generateRealtimeSummary,
    transcribeChunk,
  }
}
